import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaLock, FaCheck } from "react-icons/fa";
import { SparklesIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { ArrowLeft, GraduationCap, Target, X, Moon, Sun } from "lucide-react";
import CircularProgress from "@mui/material/CircularProgress";
import API_BASE_URL from "../Config/config";
import { useTenant } from "../Context/TenantContext";
import { useTheme } from "../contexts/ThemeContext";
import { hasFullAccess } from "../utils/paymentStatus";
import { updateLearningConsistency } from '../utils/consistency';
import { UserContext } from './UserContext';

const phases = {
"Phase 1": [1, 2, 3, 4, 5],
"Phase 2": [6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
"Phase 3": [16, 17, 18, 19, 20],
};

const LANGUAGE_DATA = {
  "Python": "Master Python with 90+ levels covering basics to advanced AI & Automation.",
  "Java": "Master Java from core concepts to enterprise Spring Boot applications.",
  "AWS": "Build scalable cloud solutions with AWS certified professional level training.",
  "React": "Create modern web applications with React, Redux, and Next.js.",
  "Node.js": "Build high-performance server-side applications with Node.js and Express.",
  "SQL": "Master database management and complex query optimization with SQL.",
  "Generative AI": "Learn to build and deploy state-of-the-art AI models and LLM applications.",
  "Blockchain": "Develop secure decentralized applications and smart contracts.",
  "GoLang": "Build efficient and concurrent systems with Google's Go language.",
  "Kubernetes": "Master container orchestration and microservices deployment at scale."
};

const SkillTree = () => {
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { technology } = useParams();
  const { user: userFromContext } = useContext(UserContext);
  const userId = userFromContext?.uid || userFromContext?.id || localStorage.getItem('userId');

 const [completedLevels, setCompletedLevels] = useState([]);
 const [hasPaid, setHasPaid] = useState(false);
 const [checkingPayment, setCheckingPayment] = useState(false);
 const [showModeModal, setShowModeModal] = useState(false);
 const [selectedLevel, setSelectedLevel] = useState(null);

  useEffect(() => {
    if (userId && technology) {
      updateLearningConsistency(userId, 'DOCUMENT_OPEN', 'Elite', technology);
    }
  }, [userId, technology]);
 
 useEffect(() => {
 const fetchProgress = async () => {
 try {
 const res = await fetch(`${API_BASE_URL}/get-passed-levels/${userId}/${technology}`);
 const data = await res.json();
 if (Array.isArray(data.passedLevels)) {
 setCompletedLevels(data.passedLevels);
 }
 } catch (err) {
 console.error("❌ Failed to fetch passed levels", err);
 }
 };

 fetchProgress();
 }, [technology, userId]);

 // 🔄 Recalculate currentLevel after data is loaded
 const currentLevel = Math.max(...completedLevels, 0) + 1;

 useEffect(() => {
    if (!userId) return;

    (async () => {
      // ✅ Always grant access to student tenant or if tenant mode is active
      const user = JSON.parse(localStorage.getItem("user"));
      const isTenantStudent = user && user.role === "student" && user.tenant;

      if (isTenantStudent || tenant) {
        setHasPaid(true);
        setCheckingPayment(false);
        return;
      }

      // For regular learners, check if they've reached level 6
      if (currentLevel >= 6) {
        try {
          setCheckingPayment(true);
          const userHasFullAccess = await hasFullAccess(userId);
          setHasPaid(userHasFullAccess);
        } catch (err) {
          console.error("❌ Failed to check payment", err);
          setHasPaid(false);
        } finally {
          setCheckingPayment(false);
        }
      } else {
        setHasPaid(false);
      }
    })();
  }, [currentLevel, userId, tenant]);

 const isUnlocked = (level) => {
   if (level <= currentLevel) {
     if (level >= 6) return hasPaid;
     return true;
   }
   return false;
 };

 const isCompleted = (level) => completedLevels.includes(level);

 const handleLevelClick = (level) => {
    // Increment learning consistency when a level is opened
    if (userId) {
      updateLearningConsistency(userId, 'MCQ_START', 'Elite', technology, { level });
    }
    
    // If user reached premium levels (6+) without payment, redirect to payment screen
 if (level >= 6 && level === currentLevel && !hasPaid) {
 navigate("/skillgenpayments");
 return;
 }

 if (!isUnlocked(level)) return;

 setSelectedLevel(level);
 setShowModeModal(true);
 };

 const startMode = (selectedMode) => {
   if (!selectedLevel) return;
   
   localStorage.setItem(
     "pendingLevelComplete",
     JSON.stringify({ technology, completedLevel: selectedLevel })
   );

   if (selectedMode === 'learning') {
     // We'll pass the mode to QuestionPage via state or keep existing URL structure if it handles mode
     navigate(`/questions/${technology}/${selectedLevel}`, { state: { mode: 'learning' } });
   } else {
     navigate(`/questions/${technology}/${selectedLevel}`, { state: { mode: 'practice' } });
   }
   setShowModeModal(false);
 };

 const handleBack = () => {
   navigate("/techgrid");
 };

  return (
    <div className={`min-h-screen ${isDark ? "bg-[#0B0F1A]" : "bg-[#F8FAFC]"} text-body flex flex-col relative transition-colors duration-300`}>
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        <div
          className={`${isDark ? "bg-[#161B22] border-white/5 shadow-2xl shadow-black/50" : "bg-white border-gray-100 shadow-2xl"} p-10 rounded-[2.5rem] border w-full max-w-5xl text-center transition-colors duration-300`}
        >
          <div className="flex items-center justify-center gap-6 mb-4">
            <button
              onClick={() => handleBack()}
              className={`p-2.5 border-2 rounded-xl transition-all group shadow-sm ${
                isDark 
                ? "bg-white/5 border-white/10 text-white hover:bg-white/10" 
                : "bg-white border-primary/10 text-primary hover:border-primary hover:bg-primary/5"
              }`}
              title="Back to Explorer"
            >
              <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
            </button>
            <h1 className={`text-4xl font-black uppercase tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}>
              Skill Tree: <span className="text-primary">{technology}</span>
            </h1>
          </div>
          {LANGUAGE_DATA[technology] && (
            <p className={`${isDark ? "text-gray-400" : "text-gray-600"} text-lg mb-8 max-w-2xl mx-auto leading-relaxed`}>
              {LANGUAGE_DATA[technology]}
            </p>
          )}

          <div className="space-y-14">
            {Object.entries(phases).map(([phaseTitle, levels]) => (
              <div key={phaseTitle}>
                <h2 className="text-blue-600 text-2xl font-bold mb-6 flex items-center justify-center gap-2">
                  <SparklesIcon className="h-6 w-6" /> {phaseTitle}
                </h2>
                <div className="flex flex-wrap justify-center gap-6">
                  {levels.map((level) => {
                    const completed = isCompleted(level);
                    const unlocked = isUnlocked(level);
                    const isNextToPlay = level === currentLevel;
                    const isPaywallLevel =
                      level >= 6 && !hasPaid;

                    let bgClass = isDark ? "bg-white/5 text-gray-600 border border-white/5" : "bg-gray-50 text-gray-300 border border-gray-100";
                    let icon = null;
                    let extraStyle = "";
                    let tooltip = "";

                    if (completed) {
                      bgClass = "bg-blue-600 text-white shadow-blue-400/50 shadow-lg";
                      icon = <FaCheck />;
                      tooltip = "Completed";
                    } else if (unlocked) {
                      bgClass =
                        "bg-blue-400 text-white hover:scale-110 shadow-xl transition transform duration-300";
                      tooltip = "Click to begin";
                    } else if (
                      level >= 6 &&
                      level === currentLevel &&
                      !hasPaid
                    ) {
                      bgClass = isDark 
                        ? "bg-white/5 text-gray-500 border-2 border-dashed border-white/10"
                        : "bg-gray-100 text-gray-400 border-2 border-dashed border-gray-200";
                      icon = <FaLock />;
                      tooltip = "Unlock full access across SkillGenie – click to subscribe";
                      extraStyle = "animate-pulse";
                    } else {
                      bgClass = isDark ? "bg-white/5 text-gray-600 border border-white/5" : "bg-gray-50 text-gray-300 border border-gray-100";
                      icon = <FaLock />;
                      tooltip = `Pass Level ${level - 1} to unlock`;
                    }

                    // ✨ Add glow effect for the next playable level
                    if (isNextToPlay && unlocked) {
                      bgClass = "bg-blue-500 text-white shadow-xl scale-110 ring-4 ring-blue-100/20";
                    }

                    return (
                      <div
                        key={level}
                        onClick={() => handleLevelClick(level)}
                        className={`relative w-16 h-16 rounded-2xl flex items-center justify-center font-bold text-lg transition-all ${bgClass} ${extraStyle} ${
                          unlocked || isPaywallLevel
                            ? "cursor-pointer"
                            : "cursor-not-allowed opacity-50"
                        }`}
                        title={tooltip}
                      >
                        {icon ? icon : level}
                        {/* Optional Glow Badge */}
                        {isNextToPlay && unlocked && (
                          <span className="absolute top-[-8px] right-[-8px] bg-blue-600 text-white text-[10px] p-1 rounded-full shadow-lg animate-bounce">
                            ⚡
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {checkingPayment && (
          <div className={`${isDark ? "bg-gray-900/80 border-white/10 text-white" : "bg-white/80 border-gray-100 text-primary"} absolute bottom-10 left-1/2 -translate-x-1/2 text-sm animate-pulse flex items-center gap-2 px-4 py-2 rounded-full shadow-lg border backdrop-blur-sm z-10`}>
            <CircularProgress size={16} color="inherit" /> Checking payment status...
          </div>
        )}
      </div>

      {/* Mode Selection Modal */}
      {showModeModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className={`${isDark ? "bg-[#161B22] border border-white/10" : "bg-white"} rounded-[2.5rem] w-full max-w-md overflow-hidden shadow-2xl animate-in zoom-in duration-300 relative`}>
            <button 
              onClick={() => setShowModeModal(false)}
              className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>

            <div className="p-10">
              <div className="text-center mb-10">
                <h2 className={`text-2xl font-black uppercase tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}>Level {selectedLevel}</h2>
                <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mt-1">Select Mode</p>
                <div className="h-1 w-12 bg-blue-600 mx-auto mt-4 rounded-full"></div>
              </div>
              
              <div className="space-y-4">
                <button 
                  onClick={() => startMode('learning')}
                  className={`w-full flex items-center gap-5 p-6 rounded-3xl border-2 transition-all group ${
                    isDark 
                    ? "border-white/5 bg-white/5 hover:border-blue-600 hover:bg-blue-600/10" 
                    : "border-gray-100 hover:border-blue-600 hover:bg-blue-50"
                  }`}
                >
                  <div className={`p-4 rounded-2xl transition-all shadow-sm ${
                    isDark ? "bg-white/5 text-gray-500 group-hover:bg-blue-600 group-hover:text-white" : "bg-gray-50 text-gray-400 group-hover:bg-blue-600 group-hover:text-white"
                  }`}>
                    <GraduationCap size={28} />
                  </div>
                  <div className="text-left">
                    <h4 className={`font-bold text-lg ${isDark ? "text-white" : "text-gray-900"}`}>Learning Mode</h4>
                    <p className="text-xs text-gray-500 font-medium">Study with AI explanations</p>
                  </div>
                </button>

                <button 
                  onClick={() => startMode('practice')}
                  className={`w-full flex items-center gap-5 p-6 rounded-3xl border-2 transition-all group ${
                    isDark 
                    ? "border-white/5 bg-white/5 hover:border-blue-600 hover:bg-blue-600/10" 
                    : "border-gray-100 hover:border-blue-600 hover:bg-blue-50"
                  }`}
                >
                  <div className={`p-4 rounded-2xl transition-all shadow-sm ${
                    isDark ? "bg-white/5 text-gray-500 group-hover:bg-blue-600 group-hover:text-white" : "bg-gray-50 text-gray-400 group-hover:bg-blue-600 group-hover:text-white"
                  }`}>
                    <Target size={28} />
                  </div>
                  <div className="text-left">
                    <h4 className={`font-bold text-lg ${isDark ? "text-white" : "text-gray-900"}`}>Practice Mode</h4>
                    <p className="text-xs text-gray-500 font-medium">Test & Evaluation Mode</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillTree;



